import { useState } from "react";
import { notificationsClient } from "../apiClient/notificationsClient";
import { ApiError } from "../apiClient/apiRequest";
import type { BroadcastResult } from "../apiClient/notificationsClient";

/** Inline "Notify all employees" trigger + form — broadcasts to everyone, confirmed before
 *  sending since it reaches the whole company. Defaults are editable before send; re-opening
 *  after a close resets back to the defaults. Used next to admin actions (top-ups, coupons,
 *  new benefits) so announcing what just happened is one click away. */
export function NotifyAllControl({ subjectDefault, messageDefault }: { subjectDefault: string; messageDefault: string }) {
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState(subjectDefault);
  const [message, setMessage] = useState(messageDefault);
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<BroadcastResult | null>(null);

  function handleOpen() {
    setSubject(subjectDefault);
    setMessage(messageDefault);
    setError(null);
    setResult(null);
    setOpen(true);
  }

  async function handleSend() {
    setSending(true);
    setError(null);
    try {
      const res = await notificationsClient.broadcast(subject.trim(), message.trim());
      setResult(res);
      setConfirming(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't send this notification.");
    } finally {
      setSending(false);
    }
  }

  if (!open) {
    return (
      <button type="button" className="btn btn-ghost" onClick={handleOpen}>
        Notify all employees
      </button>
    );
  }

  return (
    <div className="form notify-inline-form">
      <div className="form-field">
        <label>Subject</label>
        <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)} disabled={sending} />
      </div>
      <div className="form-field">
        <label>Message</label>
        <textarea rows={4} value={message} onChange={(e) => setMessage(e.target.value)} disabled={sending} />
      </div>
      {error && <div className="form-banner form-banner--error">{error}</div>}
      {result && (
        <div className="form-banner">
          Sent to {result.sent} of {result.recipientCount} employee(s).
          {result.failed > 0 && ` ${result.failed} failed to send.`}
        </div>
      )}
      <div className="form-actions">
        {confirming ? (
          <div className="cancel-confirm">
            <span>Emails every employee right now. Sure?</span>
            <button type="button" className="btn btn-ghost" onClick={() => setConfirming(false)} disabled={sending}>
              No
            </button>
            <button type="button" className="btn btn-danger" onClick={handleSend} disabled={sending}>
              {sending ? "Sending…" : "Yes, send"}
            </button>
          </div>
        ) : (
          <>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setConfirming(true)}
              disabled={!subject.trim() || !message.trim()}
            >
              Send to all employees
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => setOpen(false)} disabled={sending}>
              Close
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/** Inline "Notify" trigger + form for one specific employee — no confirm step, since the
 *  blast radius is a single person rather than the whole company. */
export function NotifyOneControl({
  email,
  subjectDefault,
  messageDefault,
}: {
  email: string;
  subjectDefault: string;
  messageDefault: string;
}) {
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState(subjectDefault);
  const [message, setMessage] = useState(messageDefault);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  function handleOpen() {
    setSubject(subjectDefault);
    setMessage(messageDefault);
    setError(null);
    setDone(false);
    setOpen(true);
  }

  async function handleSend() {
    if (!subject.trim() || !message.trim()) {
      setError("Provide both a subject and a message.");
      return;
    }
    setSending(true);
    setError(null);
    try {
      await notificationsClient.sendToEmployee(email, subject.trim(), message.trim());
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't send this notification.");
    } finally {
      setSending(false);
    }
  }

  if (!open) {
    return (
      <button type="button" className="btn btn-ghost" onClick={handleOpen}>
        Notify
      </button>
    );
  }

  return (
    <div className="form notify-inline-form">
      <div className="form-field">
        <label>Subject</label>
        <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)} disabled={sending} />
      </div>
      <div className="form-field">
        <label>Message</label>
        <textarea rows={4} value={message} onChange={(e) => setMessage(e.target.value)} disabled={sending} />
      </div>
      {error && <div className="form-banner form-banner--error">{error}</div>}
      {done && <div className="form-banner">Sent.</div>}
      <div className="form-actions">
        <button type="button" className="btn btn-primary" onClick={handleSend} disabled={sending}>
          {sending ? "Sending…" : "Send"}
        </button>
        <button type="button" className="btn btn-ghost" onClick={() => setOpen(false)} disabled={sending}>
          Close
        </button>
      </div>
    </div>
  );
}
